package one.devsbox.shared.files

import kotlin.math.abs

@Suppress("ClassName")
sealed class AspectRatio(
    val w: Int,
    val h: Int
) {

    data object _21_9 : AspectRatio(21, 9)
    data object _16_10 : AspectRatio(16, 10)
    data object _16_9 : AspectRatio(16, 9)
    data object _5_4 : AspectRatio(5, 4)
    data object _4_3 : AspectRatio(4, 3)
    data object _3_2 : AspectRatio(3, 2)
    data object _2_1 : AspectRatio(2, 1)

    data object _9_21 : AspectRatio(9, 21)
    data object _10_16 : AspectRatio(10, 16)
    data object _9_16 : AspectRatio(9, 16)
    data object _4_5 : AspectRatio(4, 5)
    data object _3_4 : AspectRatio(3, 4)
    data object _2_3 : AspectRatio(2, 3)
    data object _1_2 : AspectRatio(1, 2)
    data object _1_1 : AspectRatio(1, 1)

    class Custom(width: Int, height: Int) : AspectRatio(width, height)

    override fun toString() = "$w:$h"
    fun label() = "$w:$h"


    fun reverse(): AspectRatio {
        return all.firstOrNull { it.w == h && it.h == w } ?: Custom(h, w)
    }

    companion object {
        val horizontal get() = listOf(_21_9, _16_10, _16_9, _5_4, _4_3, _3_2, _2_1, _1_1)
        val vertical
            get() = listOf(
                _9_21, _10_16, _9_16, _4_5, _3_4, _2_3, _1_2,

                )
        val all get() = horizontal + vertical

        fun aspectRatio(width: Int, height: Int): AspectRatio {
            if (width == height) return _1_1
            if ((minOf(width, height).toDouble() / maxOf(width, height).toDouble()) > 0.9) return _1_1

            return calculateAspectRatio(width, height).let { new ->
                all.firstOrNull { it.w == new.first && it.h == new.second }
                    ?: Custom(new.first, new.second)
            }
        }

        fun closestStandard(pair: String): AspectRatio {
            val (w, h) = pair.split(":").map { it.toInt() }
            return closestStandard(w, h)
        }

        fun closestStandard(width: Int, height: Int): AspectRatio {
            val result = aspectRatio(width, height)

            if (result is Custom) {
                // Prevent division by zero if height is 0
                if (result.h == 0 || result.w == 0) return _16_9

                val targetRatio = result.w.toDouble() / result.h

                return all.minByOrNull {
                    abs((it.w.toDouble() / it.h) - targetRatio)
                } ?: result
            }

            return result
        }

        fun calculateAspectRatio(width: Int, height: Int): Pair<Int, Int> {
            return runCatching {
                if (width == height) return 1 to 1
                if (minOf(width, height).toDouble() / maxOf(width, height).toDouble() > 0.9) return 1 to 1

                val gcd = calculateGCD(width, height)
                val w = width / gcd
                val h = height / gcd
                when (w to h) {
                    8 to 5 -> 16 to 10
                    5 to 8 -> 10 to 16
                    7 to 3 -> 21 to 9
                    3 to 7 -> 9 to 21
                    else -> w to h
                }
            }.getOrNull() ?: (0 to 0)
        }

        fun calculateGCD(a: Int, b: Int): Int {
            var num1 = a
            var num2 = b
            while (num2 != 0) {
                val temp = num2
                num2 = num1 % num2
                num1 = temp
            }
            return num1
        }
    }


}
